import {
  createTableColumn,
  makeStyles,
  Table,
  TableBody,
  TableRow,
  TableSelectionCell,
  useTableFeatures,
  useTableSelection,
} from '@fluentui/react-components';
import type { TableRowProps } from '@fluentui/react-components';
import type { Dispatch, KeyboardEvent, MouseEvent, SetStateAction } from 'react';

import { getItemPathByItemType } from '@modules/documents';
import { getItemTypeLabel } from '@modules/items';
import type { TItem } from '@modules/items';
import { ControlledTooltip, SmartTableCell, ViewDialogItemButton } from '@shared/components';
import { buildHref } from '@shared/utils';
import type { TSelectableDialogItem } from './selectItemDialogItems';

const useStyles = makeStyles({
  tableBody: {
    '& > :last-child': {
      borderBottom: 'none',
    },
  },
});

type TProps = {
  items: TSelectableDialogItem[];
  selectableItems: TSelectableDialogItem[];
  localSelectedItems: ReadonlyMap<string, TItem>;
  setLocalSelectedItems: Dispatch<SetStateAction<ReadonlyMap<string, TItem>>>;
  isFolderSelectable: boolean;
  multiSelect: boolean;
  onNavigateFolder?: (folderId: string) => void;
  onPickItem?: (item: TItem) => void | Promise<void>;
  getDescriptionHtml?: (item: TSelectableDialogItem) => string | undefined;
};

const SelectItemRows = ({
  items,
  selectableItems,
  localSelectedItems,
  setLocalSelectedItems,
  isFolderSelectable,
  multiSelect,
  onNavigateFolder,
  onPickItem,
  getDescriptionHtml,
}: TProps) => {
  const classes = useStyles();

  const {
    getRows,
    selection: { toggleRow, isRowSelected },
  } = useTableFeatures(
    {
      columns: [
        createTableColumn<TSelectableDialogItem>({ columnId: 'selection' }),
        createTableColumn<TSelectableDialogItem>({ columnId: 'name' }),
      ],
      items,
      getRowId: (item) => item._id,
    },
    [
      useTableSelection({
        selectionMode: multiSelect ? 'multiselect' : 'single',
        selectedItems: localSelectedItems.keys(),
        onSelectionChange: (_e, data) => {
          const newSelected = new Map(multiSelect ? localSelectedItems : []);
          selectableItems.forEach((item) => {
            if (data.selectedItems.has(item._id)) {
              newSelected.set(item._id, item);
            } else {
              newSelected.delete(item._id);
            }
          });
          setLocalSelectedItems(newSelected);
        },
      }),
    ]
  );

  const rows = getRows((row) => {
    const selected = isRowSelected(row.rowId);
    const item = row.item;
    const toggle = (event: MouseEvent | KeyboardEvent) => {
      // Fluent's single selection never unselects a row on a second click.
      if (!multiSelect && selected) setLocalSelectedItems(new Map());
      else toggleRow(event, row.rowId);
    };
    return {
      ...row,
      onClick: (event: MouseEvent) => {
        if (item.isFolderType && !isFolderSelectable) {
          onNavigateFolder?.(item._id);
          return;
        }

        if (!item.isSelectable) return;

        if (!multiSelect && onPickItem) {
          void onPickItem(item);
          return;
        }

        toggle(event);
      },
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;

        event.preventDefault();
        if (item.isFolderType || !item.isSelectable) return;

        if (!multiSelect && onPickItem) {
          void onPickItem(item);
          return;
        }

        toggle(event);
      },
      selected,
      appearance: (selected ? 'brand' : 'none') as TableRowProps['appearance'],
    };
  });

  return (
    <Table>
      <TableBody className={classes.tableBody}>
        {rows.map(({ item, selected, onClick, onKeyDown, appearance }) => {
          const tooltipContent = item.disabledReason
            ? item.disabledReason
            : !item.isAccessible && item.isTypeSelectable
              ? 'You don\'t have "Manage" access for this item.'
              : '';

          return (
            <ControlledTooltip
              key={item._id}
              enable={Boolean(tooltipContent)}
              relationship="inaccessible"
              content={tooltipContent}
              positioning="before"
              withArrow
            >
              <TableRow
                onClick={onClick}
                onKeyDown={onKeyDown}
                aria-disabled={!item.isSelectable}
                aria-selected={selected}
                appearance={appearance}
                className="cursor-pointer"
              >
                <TableSelectionCell
                  checked={selected}
                  type={multiSelect ? 'checkbox' : 'radio'}
                  radioIndicator={{ 'aria-label': 'Select item' }}
                  className={item.isSelectable ? '' : '!opacity-0'}
                />
                <SmartTableCell
                  variant={{ kind: 'item', item }}
                  descriptionHtml={getDescriptionHtml?.(item)}
                  onLinkClick={item.isFolderType && onNavigateFolder ? () => onNavigateFolder(item._id) : undefined}
                  linkDisabled={!item.isFolderType && !item.isSelectable}
                  forcePlainText={!item.isFolderType}
                  tooltip={{ positioning: 'after', threshold: 60 }}
                  after={
                    !item.isFolderType ? (
                      <ViewDialogItemButton
                        href={buildHref(`/${getItemPathByItemType(item.type)}/${item._id}`)}
                        tooltipContent={`View ${getItemTypeLabel(item)} in new tab`}
                      />
                    ) : null
                  }
                />
              </TableRow>
            </ControlledTooltip>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default SelectItemRows;
